import { getNatsClient } from '@khal-os/sdk/app';
import { useMemo } from 'react';
import { create } from 'zustand';
import type { DesktopEntry, DesktopRuntimeKind } from '../types/desktop-entry';

/**
 * Identity remap for official Namastex apps. The kernel's `app_store` table
 * is seeded with names and icon paths that don't always match the launcher's
 * conventions — e.g. "File Manager" vs the launcher's "Files", or icon paths
 * like `/icons/folder.svg` that don't exist in the desktop's `public/` (the
 * Vite SPA fallback then serves them as `text/html`, which the browser
 * silently fails to decode and renders as a broken image).
 *
 * This map is the single source of truth for first-party app identity used by
 * BOTH the launcher (`mapRowToDesktopEntry` below) and the App Store
 * (`marketplace/AppCard.tsx`). Third-party apps (no entry here) still fall
 * through to the kernel-supplied values.
 */
export interface BuiltinDisplay {
	name: string;
	icon: string;
}

export const BUILTIN_DISPLAY: Record<string, BuiltinDisplay> = {
	terminal: { name: 'Terminal', icon: '/icons/dusk/terminal.svg' },
	files: { name: 'Files', icon: '/icons/dusk/finder.svg' },
	settings: { name: 'Settings', icon: '/icons/dusk/system_preferences.svg' },
	'nats-viewer': { name: 'NATS Viewer', icon: '/icons/dusk/activity_monitor.svg' },
	'mission-control': { name: 'Mission Control', icon: '/icons/dusk/mission_control.svg' },
	genie: { name: 'Genie', icon: '/icons/dusk/automator.svg' },
	ideas: { name: 'Ideas', icon: '/icons/dusk/notes.svg' },
	marketplace: { name: 'App Store', icon: '/icons/dusk/app_store.svg' },
};

/** Resolve the canonical display name + icon for a slug. Returns undefined for third-party apps. */
export function resolveBuiltinDisplay(slug: string): BuiltinDisplay | undefined {
	return BUILTIN_DISPLAY[slug];
}


/**
 * Bundled apps — always visible regardless of NATS/PG state.
 * Everything else is installable via the marketplace.
 */
const BUNDLED_APPS: DesktopEntry[] = [
	{
		id: 'files',
		name: 'Files',
		icon: '/icons/dusk/finder.svg',
		exec: null,
		type: 'builtin',
		component: 'files',
		categories: ['System'],
		comment: 'File browser',
		onDesktop: true,
	},
	{
		id: 'settings',
		name: 'Settings',
		icon: '/icons/dusk/system_preferences.svg',
		exec: null,
		type: 'builtin',
		component: 'settings',
		categories: ['System'],
		comment: 'Desktop settings',
		onDesktop: true,
	},
	{
		id: 'marketplace',
		name: 'App Store',
		icon: '/icons/dusk/app_store.svg',
		exec: null,
		type: 'builtin',
		component: 'marketplace',
		categories: ['System'],
		comment: 'Browse and install apps',
		onDesktop: true,
	},
];

/** IDs of bundled apps — used to deduplicate when merging with installed apps. */
const BUNDLED_IDS = new Set(BUNDLED_APPS.map((a) => a.id));

/** Shape of an installed app row joined with app_store from the NATS apps.list response. */
export interface InstalledAppRow {
	/** `installed_apps.id` (UUID) — required by AppSettingsPanel for envClient
	 *  targeting (`os.app.config.*` calls take installedAppId per Decision 9e
	 *  in the app-env-management wish). Optional in the type so older kernel
	 *  replies (pre-core#336) don't break parsing. */
	installedAppId?: string;
	slug: string;
	path: string;
	status: string;
	/** `installed_apps.lifecycle_state` enum — drives LifecycleBadge dots on
	 *  launcher icons + LifecycleBanner on open windows. Optional for the same
	 *  back-compat reason as `installedAppId`. */
	lifecycleState?: import('../types/lifecycle').LifecycleState;
	/** `installed_apps.manifest_json` — the pack's khal-app.json captured at
	 *  install time. Carries `env: []` schema for the Environment tab + any
	 *  install-time pack metadata. Optional / unknown shape; consumers narrow. */
	installedManifestJson?: unknown;
	// -- Runtime v2 fields (additive) --
	/** Runtime kind from installed_apps/app_store/manifest. */
	runtimeKind?: DesktopRuntimeKind | string;
	/** The per-install subdomain host for v2 image apps (installed_apps.subdomain_host). */
	launchUrl?: string;
	/** One-time launch token handle for v2 auth handshake. */
	launchToken?: string;
	store: {
		name: string;
		slug: string;
		iconUrl: string | null;
		iconLucide: string | null;
		shortDescription: string | null;
		category: string | null;
		manifestJson: unknown;
		permission: string | null;
		natsPrefix: string | null;
		defaultWidth: number | null;
		defaultHeight: number | null;
		fullSizeContent: boolean | null;
		minRole: string | null;
	} | null;
}

function asObject(value: unknown): Record<string, unknown> | null {
	return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function readString(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function readManifestRuntime(manifestJson: unknown): string | undefined {
	const root = asObject(manifestJson);
	if (!root) return undefined;
	const spec = asObject(root.spec);
	return readString(root.runtimeKind) ?? readString(root.runtime) ?? readString(spec?.runtime);
}

function readManifestLaunchUrl(manifestJson: unknown): string | undefined {
	const root = asObject(manifestJson);
	if (!root) return undefined;
	const spec = asObject(root.spec);
	return readString(root.launchUrl) ?? readString(spec?.launchUrl);
}

export function normalizeRuntimeKind(value: unknown): 'v1-bundle' | 'v2-iframe' | undefined {
	const raw = readString(value)?.toLowerCase();
	if (!raw) return undefined;
	if (raw === 'v1-bundle' || raw === 'bundle') return 'v1-bundle';
	if (raw === 'v2-iframe' || raw === 'v2-image' || raw === 'iframe' || raw === 'image') return 'v2-iframe';
	return undefined;
}

export function resolveRuntimeKind(...candidates: unknown[]): 'v1-bundle' | 'v2-iframe' | undefined {
	let sawV1 = false;
	for (const candidate of candidates) {
		const runtimeKind = normalizeRuntimeKind(candidate);
		if (runtimeKind === 'v2-iframe') return 'v2-iframe';
		if (runtimeKind === 'v1-bundle') sawV1 = true;
	}
	return sawV1 ? 'v1-bundle' : undefined;
}

function resolveRowRuntimeKind(row: InstalledAppRow): 'v1-bundle' | 'v2-iframe' | undefined {
	return resolveRuntimeKind(
		row.runtimeKind,
		readManifestRuntime(row.installedManifestJson),
		readManifestRuntime(row.store?.manifestJson),
	);
}

export function normalizeLaunchUrl(value: unknown): string | undefined {
	const raw = readString(value);
	if (!raw) return undefined;
	if (raw.startsWith('//')) return `https:${raw}`;
	if (/^https?:\/\//i.test(raw)) return raw;
	if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return undefined;
	return `https://${raw.replace(/^\/+/, '')}`;
}

function resolveLaunchUrl(row: InstalledAppRow): string | undefined {
	return (
		normalizeLaunchUrl(row.launchUrl) ??
		normalizeLaunchUrl(readManifestLaunchUrl(row.installedManifestJson)) ??
		normalizeLaunchUrl(readManifestLaunchUrl(row.store?.manifestJson))
	);
}

function normalizeInstalledAppRow(row: InstalledAppRow): InstalledAppRow {
	return {
		...row,
		runtimeKind: resolveRowRuntimeKind(row),
		launchUrl: resolveLaunchUrl(row),
	};
}

/** Extract bundleUrl from the app_store manifestJson if present. */
function extractBundleUrl(manifestJson: unknown): string | undefined {
	if (manifestJson && typeof manifestJson === 'object' && 'bundleUrl' in manifestJson) {
		const val = (manifestJson as Record<string, unknown>).bundleUrl;
		return typeof val === 'string' ? val : undefined;
	}
	return undefined;
}

/** Map a PG installed-app row to a DesktopEntry for the launcher. */
function mapRowToDesktopEntry(row: InstalledAppRow): DesktopEntry {
	const bundleUrl = extractBundleUrl(row.store?.manifestJson);
	const builtin = resolveBuiltinDisplay(row.slug);
	const runtimeKind = resolveRowRuntimeKind(row);
	return {
		id: row.slug,
		name: builtin?.name ?? row.store?.name ?? row.slug,
		icon: builtin?.icon ?? row.store?.iconUrl ?? row.store?.iconLucide ?? '/icons/dusk/default.svg',
		exec: null,
		type: 'installed',
		component: row.slug,
		categories: row.store?.category ? [row.store.category] : ['System'],
		comment: row.store?.shortDescription ?? null,
		onDesktop: true,
		bundleUrl,
		lifecycleState: row.lifecycleState,
		// Runtime v2 fields (additive — absent for v1 apps, treated as default)
		runtimeKind,
		launchUrl: resolveLaunchUrl(row),
		launchToken: row.launchToken,
		defaultWidth: row.store?.defaultWidth ?? null,
		defaultHeight: row.store?.defaultHeight ?? null,
	};
}

/**
 * Per-row safe wrapper around `mapRowToDesktopEntry`. The kernel reply can
 * carry mixed shapes (built-in slugs use the manifest `id`, unstaged-disk
 * packs use the repo name as the slug, store metadata can be null, etc.).
 * One bad row must NEVER drop the entire launcher list — log it and skip.
 */
function safeMapRow(row: unknown): DesktopEntry | null {
	if (!row || typeof row !== 'object') {
		console.warn('[desktop-store] skipping non-object installed-app row', row);
		return null;
	}
	try {
		const r = row as InstalledAppRow;
		// A row without a slug cannot be addressed by the launcher / window
		// renderer (every lookup keys off `installedAppRowsBySlug[slug]`),
		// so drop it rather than ship a ghost entry with id=undefined.
		if (typeof r.slug !== 'string' || r.slug.length === 0) {
			console.warn('[desktop-store] skipping installed-app row with missing slug', row);
			return null;
		}
		return mapRowToDesktopEntry(r);
	} catch (err) {
		console.warn('[desktop-store] skipping installed-app row that failed to map', {
			row,
			error: err instanceof Error ? err.message : String(err),
		});
		return null;
	}
}

interface DesktopStore {
	apps: DesktopEntry[];
	desktopIcons: DesktopEntry[];
	wallpaper: string;
	pinnedApps: string[];
	loading: boolean;
	/** Set when fetchApps fails -- lets UI distinguish "loading" from "failed". */
	fetchError: string | null;
	/** Raw PG rows keyed by `slug`. Kept after fetchApps so AppSettingsPanel /
	 *  LifecycleBanner / WindowRenderer.getManifest can look up the installed-app
	 *  state (lifecycleState, manifest_json, installedAppId) at render time. */
	installedAppRowsBySlug: Record<string, InstalledAppRow>;

	setApps: (apps: DesktopEntry[]) => void;
	setWallpaper: (url: string) => void;
	pinApp: (appId: string) => void;
	unpinApp: (appId: string) => void;
	/** Fetch installed apps from PG via NATS. Falls back to static list on failure. */
	fetchApps: () => Promise<void>;
	/**
	 * Optional callback: called after fetchApps succeeds with the raw PG rows.
	 * Consumers (core) can register dynamic manifests and refresh role permissions.
	 */
	onAppsLoaded: ((rows: InstalledAppRow[]) => void) | null;
	setOnAppsLoaded: (cb: ((rows: InstalledAppRow[]) => void) | null) => void;
}

export const useDesktopStore = create<DesktopStore>((set, get) => ({
	apps: BUNDLED_APPS,
	desktopIcons: BUNDLED_APPS,
	wallpaper: '/wallpapers/default.svg',
	pinnedApps: [],
	loading: true,
	fetchError: null,
	onAppsLoaded: null,
	installedAppRowsBySlug: {},

	setApps: (apps) => set({ apps, desktopIcons: apps }),
	setWallpaper: (url) => set({ wallpaper: url }),
	pinApp: (appId) =>
		set((state) => ({
			pinnedApps: state.pinnedApps.includes(appId) ? state.pinnedApps : [...state.pinnedApps, appId],
		})),
	unpinApp: (appId) =>
		set((state) => ({
			pinnedApps: state.pinnedApps.filter((id) => id !== appId),
		})),

	setOnAppsLoaded: (cb) => set({ onAppsLoaded: cb }),

	fetchApps: async () => {
		try {
			const client = getNatsClient();
			const raw = await client.request('os.apps.list', undefined, 8000);
			const data = (typeof raw === 'string' ? JSON.parse(raw) : raw) as {
				apps?: unknown[];
				error?: string;
			};

			if (Array.isArray(data.apps) && data.apps.length > 0) {
				// Step 1: drop obviously broken rows (non-object, missing slug,
				// or status === 'error'). Use safeMapRow's slug check by deferring
				// validation to the map step below — we only filter `status` here.
				const accepted = data.apps.filter((r) => {
					if (!r || typeof r !== 'object') return false;
					const status = (r as { status?: unknown }).status;
					return status !== 'error';
				}) as InstalledAppRow[];

				// Step 2: index rows by slug for AppSettingsPanel / LifecycleBanner /
				// WindowRenderer.getManifest lookups. Skip rows without a slug —
				// they can never be addressed by the launcher anyway. Reading
				// `.slug` may throw on pathological rows (e.g. a getter that
				// blows up), so wrap each access in try/catch and just skip.
				const rowsBySlug: Record<string, InstalledAppRow> = {};
				for (const r of accepted) {
					try {
						if (typeof r.slug === 'string' && r.slug.length > 0) rowsBySlug[r.slug] = normalizeInstalledAppRow(r);
					} catch (slugErr) {
						console.warn(
							'[desktop-store] skipping row whose slug could not be read',
							slugErr instanceof Error ? slugErr.message : String(slugErr),
						);
					}
				}

				// Step 3: notify consumer (core) of the validated rows so it can
				// register dynamic manifests and refresh role permissions.
				const callback = get().onAppsLoaded;
				if (callback) {
					try {
						callback(accepted);
					} catch (cbErr) {
						console.warn(
							'[desktop-store] onAppsLoaded callback threw; continuing',
							cbErr instanceof Error ? cbErr.message : String(cbErr),
						);
					}
				}

				// Step 4: map each installed row defensively. A malformed row
				// (id-format quirk, null store, missing bundleUrl, …) must NOT
				// drop the rest of the list — the launcher's job is to show
				// every app it can, not nothing. Even the `BUNDLED_IDS.has(slug)`
				// check is wrapped because reading `.slug` can throw on
				// pathological rows.
				const installedEntries: DesktopEntry[] = [];
				for (const r of accepted) {
					try {
						if (BUNDLED_IDS.has(r.slug)) continue;
					} catch {
						// Slug unreadable — let safeMapRow log + skip below.
					}
					const entry = safeMapRow(r);
					if (entry) installedEntries.push(entry);
				}

				// Step 5: merge — bundled apps are unconditional, installed
				// apps are appended. BUNDLED_APPS is a non-empty const so the
				// final list can never go empty, even if every installed row
				// fails to map.
				const merged = [...BUNDLED_APPS, ...installedEntries];
				set({
					apps: merged,
					desktopIcons: merged,
					installedAppRowsBySlug: rowsBySlug,
					loading: false,
					fetchError: null,
				});
				return;
			}
		} catch (err) {
			// NATS unavailable or service not running -- keep bundled apps visible
			set({ fetchError: `Failed to load apps: ${err instanceof Error ? err.message : String(err)}` });
		}
		// Empty / failed reply: make sure bundled apps are still visible. The
		// initial state already carries them, but if a prior fetch had set
		// `apps` to something else, restore the bundled baseline rather than
		// leaving stale data on the launcher.
		const current = get().apps;
		const hasAllBundled = BUNDLED_APPS.every((a) => current.some((c) => c.id === a.id));
		if (!hasAllBundled) {
			set({ apps: BUNDLED_APPS, desktopIcons: BUNDLED_APPS });
		}
		set({ loading: false });
	},
}));

/**
 * Returns desktop apps filtered by the given permissions array.
 *
 * Mirrors the permission shapes accepted by `hasAppPermission` in
 * hooks/useLaunchApp.ts — bare id, scoped wildcard (app:*:open / app:*:*),
 * scoped per-app (app:<id>:open / app:<id>:*). Empty permissions = allow all.
 */
export function filterDesktopEntriesByPermissions(desktopIcons: DesktopEntry[], permissions: string[]): DesktopEntry[] {
	if (permissions.length === 0) return desktopIcons;
	if (permissions.includes('*')) return desktopIcons;
	const hasGlobal = permissions.includes('app:*:open') || permissions.includes('app:*:*');
	if (hasGlobal) return desktopIcons;
	return desktopIcons.filter((entry) => {
		if (entry.type === 'builtin') return true;
		const appId = entry.component ?? entry.id;
		if (permissions.includes(appId)) return true;
		if (
			permissions.includes(`app:${appId}:open`) ||
			permissions.includes(`app:${appId}:*`) ||
			permissions.includes(`app:${appId}:client`) ||
			permissions.includes(`app:${appId}:admin`)
		) {
			return true;
		}
		return false;
	});
}

export function useFilteredDesktopApps(permissions: string[]): DesktopEntry[] {
	const desktopIcons = useDesktopStore((s) => s.desktopIcons);

	return useMemo(() => filterDesktopEntriesByPermissions(desktopIcons, permissions), [desktopIcons, permissions]);
}
